import { mm } from "@inovitas/infra3dsdk";
import { GeoFrame } from "@inovitas/infra3dsdk/dist/types/mm/geoframe/geoframe";
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { defaultStore } from "shared/common/stores/defaultStore";

interface props {
  allAxis: mm.AxisObject[];
  onChange?: (value: string) => void;
  activeAxis: mm.Axis | null;
}

export default function AxisscaleSelect({
  allAxis,
  onChange,
  activeAxis,
}: props) {
  const { t } = useTranslation();

  const viewer = defaultStore((state) => state.viewer);
  const changeShowAxisscale = defaultStore(
    (state) => state.changeShowAxisscale
  );
  const clientConfig = defaultStore((state) => state.clientConfig);

  const [axisOptions, setAxisOptions] = useState<mm.AxisObject[]>([]);
  const [geoframe, setGeoframe] = useState<GeoFrame | null>(null);

  useEffect(() => {
    if (!geoframe) return;
    if (viewer?.getScene().getLRHandler().reloadingAxis) return;
    const options: mm.AxisObject[] = allAxis.filter((axis) => {
      if (!axis.axis?.isnavigationref) return false;
      const uvw = axis.axis?.xyz2uvw(
        geoframe.easting,
        geoframe.northing,
        geoframe.height
      );

      if (uvw === null) return false;
      return true;
    });

    if (options.length === 0) {
      if (allAxis.length !== 0) {
        changeShowAxisscale(false);
      }
    } else if (options.length === 1) {
      viewer
        ?.getScene()
        .getLRHandler()
        .setActiveNavigationAxis(options[0].axis);
      changeShowAxisscale(true);
    } else {
      changeShowAxisscale(true);
    }
    setAxisOptions(options);
  }, [allAxis, geoframe]);

  useEffect(() => {
    if (!viewer) return;

    const onGeoframeChanged = (geoframe: GeoFrame) => {
      setGeoframe(geoframe);
    };

    viewer.on("geoframechanged", onGeoframeChanged);

    return () => {
      viewer.off("geoframechanged", onGeoframeChanged);
    };
  }, [viewer]);

  useEffect(() => {
    if (activeAxis) {
      setAxis(activeAxis.name);
    } else {
      setAxis("");
    }
  }, [activeAxis]);

  const [axis, setAxis] = useState<string>(activeAxis?.name || "");

  const handleAxisChange = (event: SelectChangeEvent) => {
    onChange?.(event.target.value);
    setAxis(event.target.value);
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          gap: 4,
          alignItems: "center",
          paddingInlineEnd: 1,
        }}
      >
        <FormControl sx={{ minWidth: "100px" }} size="small">
          <InputLabel
            sx={{
              color: "white", // Text color
              "&.Mui-focused": {
                color: "white", // Focused text color
              },
            }}
          >
            {clientConfig?.linref?.type === "rail-ch"
              ? t("linref.kmaxis")
              : t("linref.axis")}
          </InputLabel>
          <Select
            value={axis}
            label={
              clientConfig?.linref?.type === "rail-ch"
                ? t("linref.kmaxis")
                : t("linref.axis")
            }
            onChange={handleAxisChange}
            MenuProps={{
              anchorOrigin: {
                vertical: "top",
                horizontal: "center",
              },
              transformOrigin: {
                vertical: "bottom",
                horizontal: "center",
              },
            }}
            sx={{
              maxWidth: 200,
              color: "white", // Text color
              ".MuiOutlinedInput-notchedOutline": {
                borderColor: "white", // Border color
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "white", // Hover border color
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "white", // Focus border color
              },
              "& .MuiSvgIcon-root": {
                color: "white", // Dropdown icon color
              },
              "& .MuiSelect-placeholder": {
                color: "white", // Placeholder color
              },
            }}
          >
            {axisOptions.map((option, index) => (
              <MenuItem key={index} value={option.axis?.name}>
                {option.axis?.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    </>
  );
}
